import { useState, useEffect, useCallback, useRef } from 'react';
import type { RefObject } from 'react';
import { EntityScope, ScopeTotals, UserAccount } from '../types/entity';
import { fetchUserAccounts } from '../api/entityApi';
import { entityNameCacheKey } from '@features/auth/constants/sessionStorageKeys';

export const EMPTY_SCOPE_TOTALS: ScopeTotals = { NATION: 0, DISTRICT: 0, CHURCH: 0 };

export interface UseUserAccountsOptions {
  /** Page size sent to the API; the API default (20) is used when omitted. */
  perPage?: number;
  /** Element scrolled into view when the list is collapsed with showLess. */
  listRef?: RefObject<HTMLElement | null>;
}

/** Remember every loaded entity name so headers and breadcrumbs can show it without another request. */
function cacheEntityNames(accounts: UserAccount[]) {
  for (const account of accounts) {
    if (account.scope_name) {
      sessionStorage.setItem(entityNameCacheKey(account.scope, account.scope_ref_id), account.scope_name);
    }
  }
}

/**
 * Paged list of the signed-in user's entity accounts (GET /user/account/all).
 * Page 1 loads on mount; loadMore appends the next page, showLess collapses back to page 1,
 * and setScope refetches page 1 filtered to one scope (null = all scopes).
 */
export const useUserAccounts = ({ perPage, listRef }: UseUserAccountsOptions = {}) => {
  const [accounts, setAccounts] = useState<UserAccount[]>([]);
  const [page, setPage] = useState(0);
  const [lastPage, setLastPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [pageSize, setPageSize] = useState(perPage ?? 0);
  const [scopeTotals, setScopeTotals] = useState<ScopeTotals>(EMPTY_SCOPE_TOTALS);
  const [scope, setScopeState] = useState<EntityScope | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
  const requestIdRef = useRef(0);
  const hasLoadedRef = useRef(false);

  const loadPage = useCallback(
    async (nextPage: number, nextScope: EntityScope | null, append: boolean) => {
      const requestId = ++requestIdRef.current;
      const isCurrent = () => requestId === requestIdRef.current;

      setLoadMoreError(null);
      if (append) {
        setLoadingMore(true);
      } else if (hasLoadedRef.current) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      try {
        const response = await fetchUserAccounts({
          page: nextPage,
          perPage,
          scope: nextScope ?? undefined,
        });
        if (!isCurrent()) return;

        if (response.isSuccess() && response.data) {
          const body = response.data;
          const rows = Array.isArray(body.data) ? body.data : [];
          cacheEntityNames(rows);
          setAccounts((previous) => (append ? [...previous, ...rows] : rows));
          setPage(body.current_page ?? nextPage);
          setLastPage(body.last_page ?? nextPage);
          setTotal(body.total ?? rows.length);
          setPageSize(body.per_page ?? perPage ?? rows.length);
          setScopeTotals({ ...EMPTY_SCOPE_TOTALS, ...(body.scope_totals ?? {}) });
          setError(null);
          setErrorStatus(null);
          hasLoadedRef.current = true;
        } else {
          const errorData = response.getError ? response.getError() : response.error;
          const message = errorData?.detail || 'Failed to load accounts';
          if (append) {
            setLoadMoreError(message);
          } else {
            setErrorStatus(response.status ?? null);
            setError(message);
          }
        }
      } catch {
        if (!isCurrent()) return;
        if (append) {
          setLoadMoreError('An error occurred while loading more accounts');
        } else {
          setError('An error occurred while loading accounts');
        }
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setRefreshing(false);
          setLoadingMore(false);
        }
      }
    },
    [perPage]
  );

  useEffect(() => {
    loadPage(1, null, false);
  }, [loadPage]);

  const loadMore = useCallback(() => {
    if (loading || refreshing || loadingMore || page >= lastPage) return;
    loadPage(page + 1, scope, true);
  }, [loading, refreshing, loadingMore, page, lastPage, scope, loadPage]);

  /** Collapse back to the first page without refetching, and bring the list top into view. */
  const showLess = useCallback(() => {
    requestIdRef.current += 1; // drop any in-flight "show more"
    setLoadingMore(false);
    setLoadMoreError(null);
    setAccounts((previous) => (pageSize > 0 ? previous.slice(0, pageSize) : previous));
    setPage(1);
    listRef?.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [pageSize, listRef]);

  const setScope = useCallback(
    (nextScope: EntityScope | null) => {
      setScopeState(nextScope);
      loadPage(1, nextScope, false);
    },
    [loadPage]
  );

  const reload = useCallback(() => {
    hasLoadedRef.current = false;
    return loadPage(1, scope, false);
  }, [loadPage, scope]);

  return {
    accounts,
    page,
    lastPage,
    total,
    scopeTotals,
    scope,
    hasMore: page < lastPage,
    canShowLess: page > 1,
    loading,
    refreshing,
    loadingMore,
    error,
    errorStatus,
    loadMoreError,
    loadMore,
    showLess,
    setScope,
    reload,
  };
};
